// #region Imports
import { useEffect } from "react"
import { motion } from "framer-motion"
import skills from "../../content/data/skills.json"
import { Portal } from "../../context/PortalProvider"
// #endregion


// #region Skills

const skillMap = Object.fromEntries(
    skills
        .flatMap(category => category.items)
        .map(skill => [skill.name, skill])
)

// #endregion


function ProjectPopUp({
    project,
    isOpen,
    setIsOpen
}) {

    const projectImages = project?.images ?? []


    // #region Preload Images

    useEffect(() => {
        if (!isOpen || !projectImages.length)
            return

        projectImages.forEach(src => {
            if (!src)
                return

            const img = new Image()
            img.src = src
        })
    }, [isOpen, projectImages])

    // #endregion


    if (!isOpen || !project)
        return null


    // #region Project Data

    const {
        title,
        video,
        tags = [],
        skills: projectSkills = [],
        highlight,
        links = [],
        userflow = [],
        descriptions = []
    } = project

    const images = projectImages

    const resolvedSkills = projectSkills
        .map(skill => skillMap[skill])
        .filter(Boolean)

    // #endregion


    return (
        <Portal>

            {/* BACKDROP */}

            <motion.div
                className="
                    fixed inset-0 z-[200]
                    flex items-center justify-center
                    bg-black backdrop-blur-sm
                    p-2 small:p-2
                "
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                    duration: 0.4,
                    ease: "easeOut"
                }}
            >

                {/* POPUP */}

                <motion.div
                    className="
                        bg-ivory rounded-3xl
                        h-full w-full
                        py-4
                        flex flex-col
                        items-start justify-between
                        small:rounded-xl
                    "
                    initial={{
                        opacity: 0,
                        y: 25,
                        scale: 0.98
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1
                    }}
                    transition={{
                        duration: 0.45,
                        ease: [0.22, 1, 0.36, 1]
                    }}
                >

                    {/* HEADER */}

                    <header
                        className="
                            shrink-0 min-h-20
                            px-10 py-4
                            flex items-center
                            justify-between gap-10
                            border-b-[1.5px]
                            border-charcoal/40
                            small:px-5 small:min-h-20
                            w-full
                        "
                    >

                        <section
                            className="
                                flex items-center gap-8
                                min-w-0
                                small:gap-3
                                small:flex-col
                            "
                        >

                            {/* TITLE */}

                            <aside className="flex gap-4 items-center">

                                <img
                                    src="/icons/darkStar.svg"
                                    alt="Star"
                                    className="
                                        w-8 h-8
                                        animate-spin
                                        [animation-duration:3s]
                                    "
                                />

                                <h2
                                    className="
                                        font-mada
                                        text-2xl
                                        font-medium
                                        uppercase
                                        tracking-tight
                                        small:text-xl
                                    "
                                >
                                    {title}
                                </h2>

                            </aside>


                            {/* LINKS */}

                            <aside className="gap-4 flex small:hidden">

                                {links.map(
                                    (link, i) => (
                                        <a
                                            key={i}
                                            className="
                                                flex items-center
                                                justify-center
                                                opacity-50
                                                hover:opacity-100
                                                transition-opacity
                                            "
                                            target="_blank"
                                            rel="noreferrer"
                                            title={link.name}
                                            href={link.url}
                                        >
                                            <img
                                                className="w-7 h-7"
                                                alt={link.name}
                                                src={`/icons/${link.name}Dark.svg`}
                                            />
                                        </a>
                                    )
                                )}

                            </aside>

                        </section>


                        {/* CLOSE */}

                        <button
                            title="Close"
                            onClick={() => setIsOpen(false)}
                            className="
                                group
                                flex items-center
                                justify-center
                                w-9 h-9
                                rounded-full
                                transition-all
                                duration-300
                                hover:bg-charcoal
                            "
                        >
                            <img
                                className="
                                    w-4 h-4
                                    transition-all
                                    duration-300
                                    group-hover:invert
                                "
                                alt="Close"
                                src="/icons/cross.svg"
                            />
                        </button>

                    </header>


                    {/* CONTENT */}

                    <div
                        data-lenis-prevent
                        className="
                            flex-1 min-h-0
                            w-full
                            overflow-y-auto
                            overflow-x-hidden
                            overscroll-contain
                        "
                    >

                        <section
                            className="
                                w-full
                                px-16 py-12
                                small:px-5 small:py-6
                            "
                        >

                            {/* PROJECT META */}

                            <aside
                                className="
                                    mb-12 gap-6
                                    flex flex-col
                                    w-[40%]
                                    items-start
                                    justify-between
                                    small:mb-8
                                    small:w-full
                                "
                            >

                                {/* HIGHLIGHT */}

                                {highlight && (
                                    <p
                                        className="
                                            text-2xl
                                            leading-tight
                                            text-black
                                            font-light
                                        "
                                    >
                                        {highlight}
                                    </p>
                                )}


                                {/* MOBILE LINKS */}

                                <aside className="gap-4 flex large:hidden">

                                    {links.map(
                                        (link, i) => (
                                            <a
                                                key={i}
                                                className="
                                                    flex items-center
                                                    justify-center
                                                    opacity-50
                                                    hover:opacity-100
                                                    transition-opacity
                                                "
                                                target="_blank"
                                                rel="noreferrer"
                                                title={link.name}
                                                href={link.url}
                                            >
                                                <img
                                                    className="
                                                        w-7 h-7
                                                        object-contain
                                                    "
                                                    alt={link.name}
                                                    src={`/icons/${link.name}Dark.svg`}
                                                />
                                            </a>
                                        )
                                    )}

                                </aside>


                                {/* TAGS */}

                                {tags.length > 0 && (
                                    <aside
                                        className="
                                            w-full
                                            flex flex-wrap
                                            gap-2
                                        "
                                    >

                                        {tags.map((tag, i) => (
                                            <span
                                                key={i}
                                                className="
                                                    px-3 py-1.5
                                                    border
                                                    border-charcoal/15
                                                    rounded-full
                                                    text-[10px]
                                                    small:text-sm
                                                    uppercase
                                                    tracking-wider
                                                    text-charcoal/60
                                                "
                                            >
                                                {tag}
                                            </span>
                                        ))}

                                    </aside>
                                )}


                                {/* SKILLS */}

                                {resolvedSkills.length > 0 && (
                                    <aside
                                        className="
                                            gap-3
                                            justify-start
                                            flex flex-wrap
                                            items-center
                                        "
                                    >

                                        {resolvedSkills.map(skill => (
                                            <img
                                                decoding="async"
                                                key={skill.name}
                                                src={skill.logo}
                                                alt={skill.name}
                                                title={skill.name}
                                                className="
                                                    w-6 h-6
                                                    object-contain
                                                    small:w-7
                                                    small:h-7
                                                "
                                            />
                                        ))}

                                    </aside>
                                )}

                            </aside>

                            {/* USER FLOW */}

                            {userflow?.length > 0 && (
                                <section
                                    className="
                                        w-full
                                        mb-20
                                        small:mb-12
                                    "
                                >
                                    <div
                                        className="
                                            grid
                                            grid-cols-4
                                            gap-3
                                            small:grid-cols-1
                                            small:gap-2
                                        "
                                    >

                                        {userflow.map((step, i) => (
                                            <article
                                                key={i}
                                                className="
                                                    min-h-40
                                                    p-5
                                                    rounded-2xl
                                                    border
                                                    border-charcoal/10
                                                    bg-charcoal/[0.025]
                                                    flex flex-col
                                                    justify-between
                                                    small:min-h-0
                                                    small:p-4
                                                "
                                            >

                                                <div
                                                    className="
                                                        flex
                                                        items-start
                                                        justify-between
                                                    "
                                                >

                                                    <span
                                                        className="
                                                            text-2xl
                                                            leading-none
                                                        "
                                                    >
                                                        {step.emoji}
                                                    </span>

                                                </div>

                                                <div className="mt-8 small:mt-6">

                                                    <h4
                                                        className="
                                                            text-lg
                                                            font-medium
                                                            tracking-tight
                                                            text-charcoal
                                                        "
                                                    >
                                                        {step.title}
                                                    </h4>

                                                    <p
                                                        className="
                                                            mt-2
                                                            text-sm
                                                            leading-relaxed
                                                            text-charcoal/55
                                                        "
                                                    >
                                                        {step.description}
                                                    </p>

                                                </div>

                                            </article>
                                        ))}

                                    </div>

                                </section>
                            )}


                            {/* MEDIA + DESCRIPTIONS */}

                            <div className="w-full">

                                {/* HERO VIDEO */}

                                {video && (
                                    <div
                                        className="
                                            w-full
                                            mb-20
                                            small:mb-10
                                        "
                                    >
                                        <video
                                            src={video}
                                            autoPlay
                                            muted
                                            loop
                                            playsInline
                                            preload="auto"
                                            className="
                                                w-full
                                                h-auto
                                                object-contain
                                                object-left
                                                small:h-auto
                                                small:max-h-none
                                            "
                                        />
                                    </div>
                                )}


                                {/* HERO IMAGE */}

                                {!video && images[0] && (
                                    <div
                                        className="
                                            w-full
                                            mb-20
                                            small:mb-10
                                        "
                                    >
                                        <img
                                            decoding="async"
                                            src={images[0]}
                                            alt={title}
                                            loading="eager"
                                            className="
                                                w-full
                                                h-auto
                                                object-contain
                                                object-left
                                                small:h-auto
                                                small:max-h-none
                                            "
                                        />
                                    </div>
                                )}

                                {/* DESCRIPTIONS */}

                                {descriptions.length > 0 && (
                                    <section
                                        className="
                                            w-[60%]
                                            mb-16
                                            flex flex-col
                                            gap-6
                                            small:w-full
                                            small:mb-10
                                            small:gap-5
                                        "
                                    >
                                        {descriptions.map((desc, i) => (
                                            <p
                                                key={i}
                                                className="
                                                    text-xl
                                                    leading-relaxed
                                                    text-charcoal/80
                                                "
                                            >
                                                {desc}
                                            </p>
                                        ))}
                                    </section>
                                )}


                                {/* MEDIA */}

                                {(() => {

                                    const imageOffset = video ? 0 : 1

                                    const mediaImages = images.slice(imageOffset)

                                    if (!mediaImages.length)
                                        return null

                                    return (
                                        <section
                                            className="
                                                w-full
                                                mb-24
                                                small:mb-12
                                            "
                                        >
                                            <div
                                                className="
                                                    w-full
                                                    flex flex-col
                                                    gap-8
                                                    small:gap-6
                                                "
                                            >
                                                {mediaImages.map((src, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="
                                                            w-full
                                                            flex
                                                            items-start
                                                        "
                                                    >
                                                        <img
                                                            decoding="async"
                                                            src={src}
                                                            alt={`${title} ${idx + 1}`}
                                                            loading={
                                                                idx === 0
                                                                    ? "eager"
                                                                    : "lazy"
                                                            }
                                                            className="
                                                                w-auto
                                                                h-[60vh]
                                                                max-w-full
                                                                object-contain
                                                                object-left
                                                                small:h-auto
                                                                small:max-h-none
                                                            "
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </section>
                                    )

                                })()}

                            </div>

                        </section>

                    </div>

                </motion.div>

            </motion.div>

        </Portal>
    )
}

export default ProjectPopUp